// Block D (2026-10-08): the client health panel in the CRM drawer, on the v2 kit. The score and
// its factors come from useClientHealth exactly as before; only the drawing changed. Colour is
// never the only signal: the label ("Good", "At risk") and the points are always written.
import React, { useState } from 'react';
import { Button, ProgressBar, SegmentedControl, Textarea, Field, Reveal } from '../../ui';

// The pastel a score sits on: green from 70, yellow from 40, coral below.
export const scoreTone = (score) => (score == null ? 'pastel-neutral' : score >= 70 ? 'status-success' : score >= 40 ? 'status-pending' : 'priority-high');

const TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'note', label: 'Note' },
];

function LogContactForm({ onLog, onCancel }) {
  const [type, setType] = useState('call');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  async function handleLog() {
    if (!summary.trim() || busy) return;
    setBusy(true);
    try {
      await onLog({ type, date: new Date().toISOString(), summary: summary.trim() });
      setSummary('');
    } finally { setBusy(false); }
  }
  return (
    <Reveal className="crm-section">
      <span className="crm-label">Log a contact</span>
      <SegmentedControl label="Type" options={TYPES} value={type} onChange={setType} />
      <Field label="What happened?">
        <Textarea data-reveal-focus rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Discussed the renewal proposal" />
      </Field>
      <div className="crm-row">
        <Button variant="tertiary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="confirm" size="sm" onClick={handleLog} disabled={!summary.trim() || busy}>{busy ? 'Logging…' : 'Log'}</Button>
      </div>
    </Reveal>
  );
}

/**
 * health: the result from useClientHealth (score, label, factors).
 * onLogContact(interaction): may return a promise; the form waits for it.
 * onRefresh(): recalculates.
 */
export default function ClientHealthScore({ health, onLogContact, onRefresh }) {
  const [showLog, setShowLog] = useState(false);
  if (!health) return null;
  const { score, label, factors } = health;

  async function handleLog(interaction) {
    await onLogContact(interaction);
    setShowLog(false);
  }

  return (
    <div className="crm-health">
      <div className="crm-section__head">
        <span className="crm-label">Client health score</span>
        <Button variant="tertiary" size="sm" onClick={onRefresh}>Refresh</Button>
      </div>
      <span className="crm-badge">
        <span className="crm-badge__dot" style={{ '--ui-bg': `var(--${scoreTone(score)})` }} aria-hidden="true" />
        {label}: {score} of 100 points
      </span>
      {(factors || []).map((f) => (
        <div key={f.label} className="crm-section">
          <ProgressBar label={f.label} value={f.points} max={f.max} valueText={`${f.points} of ${f.max}`} color={scoreTone(f.max > 0 ? (f.points / f.max) * 100 : 0)} />
          {f.insight && <p className="crm-muted">{f.insight}</p>}
        </div>
      ))}
      {showLog
        ? <LogContactForm onLog={handleLog} onCancel={() => setShowLog(false)} />
        : <div><Button variant="secondary" size="sm" onClick={() => setShowLog(true)}>+ Log a contact</Button></div>}
    </div>
  );
}
